import { getRuleTypeBucket } from 'utils/ruleTypeBucket';
import { coverageStatusFor } from './coverage.adaptor';
import { singleRuleFormAdaptor } from './get.adaptor';

// Bucket-keyed (see utils/ruleTypeBucket.js) — rule.type still has its
// original 6 backend values, but min_qty/max_qty/multiple/repurchase now all
// display as one "Quantity" type (they're all just different starting field
// values within the same Quantity bucket, not distinct kinds).
const TYPE_LABEL = {
  qty: 'Quantity',
  value: 'Order value',
  weight: 'Weight',
};

const SCOPE_LABEL = {
  all_store: 'All products',
  product: 'Specific products',
  collection: 'Collections',
  tag: 'Product tags',
  vendor: 'Vendors',
  product_type: 'Product types',
  price_range: 'Price range',
};

// The Status badge should reflect reality, not just the merchant's on/off
// toggle — mirrors RuleActivationService.isActiveNow() (Prime-MOQ-Backend)
// exactly: a rule the merchant switched on but scheduled for a future
// startAt isn't actually enforcing anything yet, and one past its endAt has
// already stopped, so showing a plain "Active" badge for either is
// misleading (looks like it's live right now when it isn't). Kept SEPARATE
// from `status` below (not overwritten) — bulk activate/deactivate
// eligibility in RulesTable.js needs the merchant's raw stored intent
// (status), not this real-time-computed display value; a scheduled rule is
// still "on" as far as Deactivate/Activate bulk actions are concerned.
function computeDisplayStatus(rule) {
  if (rule.status !== 'active') return 'paused';
  const now = new Date();
  if (!rule.isImmediately && rule.startAt && new Date(rule.startAt) > now) return 'scheduled';
  if (rule.endAt && new Date(rule.endAt) < now) return 'expired';
  return 'active';
}

function conditionsSummary(rule) {
  const parts = [];
  const bucket = getRuleTypeBucket(rule.type);
  if (bucket === 'qty') {
    if (rule.min) parts.push(`min ${rule.min}`);
    if (rule.max) parts.push(`max ${rule.max}`);
    if (rule.multiple) parts.push(`×${rule.multiple}`);
  } else if (bucket === 'value') {
    if (rule.minValue) parts.push(`≥ $${rule.minValue}`);
    if (rule.maxValue) parts.push(`≤ $${rule.maxValue}`);
  } else if (bucket === 'weight') {
    if (rule.minWeight) parts.push(`≥ ${rule.minWeight}${rule.weightUnit || 'lb'}`);
    if (rule.maxWeight) parts.push(`≤ ${rule.maxWeight}${rule.weightUnit || 'lb'}`);
  }
  if (rule.repurchasePeriod && rule.repurchasePeriod !== 'none') parts.push(`repurchase/${rule.repurchasePeriod}`);
  return parts.join(' · ') || '-';
}

export function getRulesListAdaptor(response) {
  const rules = (response?.data ?? []).map((rule) => {
    const displayStatus = computeDisplayStatus(rule);
    // Coverage column (docs/rule-precedence-implementation-plan.md §0): both
    // numbers are persisted by the backend's resolve job — variantsCount is
    // the conflict-adjusted claim, scopeVariantsCount the raw scope — so the
    // list never fires a per-row request for them. VARIANTS are the primary
    // count (a rule's scope is variant-granular); product counts ride along
    // only as secondary context for the tooltip.
    const coverage = {
      scopeVariants: rule.scopeVariantsCount ?? null,
      enforcingVariants: rule.variantsCount ?? null,
      scopeProducts: rule.scopeProductsCount ?? null,
      enforcingProducts: rule.productsCount ?? null,
      // LIVE, unresolved conflicts (persisted by the same resolve job),
      // NOT scopeVariants - enforcingVariants: some of that gap can be this
      // rule's own excludes (manual or automatic), a settled state, not an
      // actionable one. Deliberately NOT read by coverageStatusFor() below
      // (Partial/Full there is the gross-vs-enforcing fraction itself, on
      // purpose, see that function's own comment): this field instead
      // drives the row's progress-bar color and any live-conflict warning,
      // a separate question from whether the rule reaches its full target.
      overriddenVariants: rule.overriddenVariantsCount ?? null,
      overriddenProducts: rule.overriddenProductsCount ?? null,
    };
    return {
    id: rule.id,
    name: rule.name,
    typeLabel: TYPE_LABEL[getRuleTypeBucket(rule.type)],
    scopeLabel: SCOPE_LABEL[rule.scopeType] ?? rule.scopeType,
    conditions: conditionsSummary(rule),
    startAt: rule.startAt,
    endAt: rule.endAt,
    status: rule.status,
    displayStatus,
    coverage,
    // Computed regardless of displayStatus: RuleRow.js mutes the badge to
    // gray for a non-active row instead of hiding it (user's own ask: show
    // the same Full/Partial/Inactive verdict for a deactivated rule too,
    // just visually muted).
    coverageStatus: coverageStatusFor(coverage),
    // All-time count of blocked ADD-TO-CART attempts for this rule
    // (RuleStatsDaily, rolled up hourly — see rule.service.ts's
    // getShopRules()). Deliberately not "invalid orders": a checkout blocked
    // by the Shopify Function never becomes an order (the Function is fully
    // sandboxed, no DB/HTTP access to report back to the app), so this is
    // the closest real, already-tracked signal for "this rule is blocking
    // something."
    invalidAttemptsCount: rule.invalidAttemptsCount ?? 0,
    // Shoppers who set a quantity this rule rejects and never clicked —
    // deliberately its own number, never folded into invalidAttemptsCount
    // (see the backend's InvalidAddToCartAttemptMongo `intent` field).
    blockedIntentCount: rule.blockedIntentCount ?? 0,
    // GET /rules already includes the same scope relations (products,
    // collections, tags, ...) as GET /rules/:id (see rule.service.ts's
    // shared SCOPE_CHILD_INCLUDE) — reuse the single-rule adaptor here so
    // list consumers (RuleProductsCell.js, bulk status update) get a
    // ready-to-use form-shape rule for free, no extra per-row GET needed.
    full: singleRuleFormAdaptor(rule).data,
    };
  });

  return {
    data: rules,
    pageInfo: response?.pageInfo ?? { total: rules.length, hasNextPage: false },
  };
}
